import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { Footer } from './Footer';
import { RoutePermissionGuard } from '@/app/router/RoutePermissionGuard';

const MOBILE_QUERY = '(max-width: 991.98px)';

// Mirrors the noa Application layout and reproduces its responsive sidebar
// behavior (normally done by noa's JS): on mobile the sidebar is off-canvas and
// toggled via html[data-toggled] + the #responsive-overlay backdrop.
export function AppLayout() {
  const [isMobile, setIsMobile] = useState(() => window.matchMedia(MOBILE_QUERY).matches);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const onChange = () => {
      setIsMobile(mq.matches);
      if (!mq.matches) setOpen(false);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    const html = document.documentElement;
    if (isMobile) {
      // Mobile: noa hides the sidebar only with data-toggled="close" (translateX
      // off-screen); with NO attribute it stays visible. So set "close" to hide.
      html.setAttribute('data-toggled', open ? 'open' : 'close');
    } else {
      // Desktop: collapse the sidebar to icon-only (noa vertical/overlay value).
      if (open) html.setAttribute('data-toggled', 'icon-overlay-close');
      else html.removeAttribute('data-toggled');
    }
    return () => html.removeAttribute('data-toggled');
  }, [isMobile, open]);

  // Mobile: close the off-canvas sidebar on any tap outside it (robust on touch;
  // the overlay alone wasn't reliably catching taps).
  useEffect(() => {
    if (!(isMobile && open)) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.closest('#sidebar') || t.closest('.sidemenu-toggle'))) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [isMobile, open]);

  return (
    <div className="page">
      <Header onToggleSidebar={() => setOpen((v) => !v)} />
      <Sidebar onNavigate={() => isMobile && setOpen(false)} />
      <div className="main-content app-content">
        <div className="container-fluid p-0">
          {/* pb-4 keeps page content from sitting flush against the footer (a
              wrapper so it's immune to the parent's p-0 !important). */}
          <div className="pb-4">
            <RoutePermissionGuard>
              <Outlet />
            </RoutePermissionGuard>
          </div>
        </div>
      </div>
      <Footer />
      <div
        id="responsive-overlay"
        className={isMobile && open ? 'active' : ''}
        onClick={() => setOpen(false)}
      />
    </div>
  );
}
